import React, { Suspense, lazy } from 'react';
import { QueryClient, QueryClientProvider, QueryCache, MutationCache } from '@tanstack/react-query';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { trpc, trpcClient } from './trpc/client';
import { AuthProvider } from './contexts/AuthContext';
import { LocationProvider } from './contexts/LocationContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { ToastProvider } from './contexts/ToastContext';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { Layout } from './components/layout/Layout';
import { BillingLayout } from './components/billing/BillingLayout';
import { LocationRedirect } from './components/layout/LocationRedirect';
import { LocationSync } from './components/layout/LocationSync';
import { LegacyRedirect } from './components/layout/LegacyRedirect';
import { Skeleton } from './components/ui/Skeleton';
import { ErrorBoundary } from './components/ui/ErrorBoundary';

/**
 * Lazy import with automatic retry on chunk loading failure.
 * After a deployment, old chunk URLs become stale. This retries once
 * and reloads the page if the chunk still can't be loaded.
 */
function lazyWithRetry<T extends React.ComponentType>(
  factory: () => Promise<{ default: T }>
) {
  return lazy(() =>
    factory().catch(() => {
      // Chunk failed to load — likely a stale build. Reload once.
      const reloaded = sessionStorage.getItem('vitbikes_chunk_reload');
      if (!reloaded) {
        sessionStorage.setItem('vitbikes_chunk_reload', '1');
        window.location.reload();
        // Return a never-resolving promise to prevent render during reload
        return new Promise<{ default: T }>(() => {});
      }
      // Already reloaded once — clear flag and let the error propagate
      sessionStorage.removeItem('vitbikes_chunk_reload');
      return factory();
    })
  );
}

// Clear reload flag on successful page load
sessionStorage.removeItem('vitbikes_chunk_reload');

// Lazy load pages for code splitting (with retry on stale chunks)
const Login = lazyWithRetry(() => import('./pages/Login').then(m => ({ default: m.Login })));
const Dashboard = lazyWithRetry(() => import('./pages/Dashboard').then(m => ({ default: m.Dashboard })));
const Contacts = lazyWithRetry(() => import('./pages/Contacts').then(m => ({ default: m.Contacts })));
const Calendar = lazyWithRetry(() => import('./pages/Calendar').then(m => ({ default: m.Calendar })));
const Statistics = lazyWithRetry(() => import('./pages/Statistics').then(m => ({ default: m.Statistics })));
const Reports = lazyWithRetry(() => import('./pages/Reports').then(m => ({ default: m.Reports })));
const Admin = lazyWithRetry(() => import('./pages/Admin').then(m => ({ default: m.Admin })));
const Settings = lazyWithRetry(() => import('./pages/Settings').then(m => ({ default: m.Settings })));
const Schulung = lazyWithRetry(() => import('./pages/Schulung').then(m => ({ default: m.Schulung })));
const TwoFactorSetup = lazyWithRetry(() => import('./pages/TwoFactorSetup').then(m => ({ default: m.default })));
const Support = lazyWithRetry(() => import('./pages/Support').then(m => ({ default: m.Support })));
const RcsTest = lazyWithRetry(() => import('./pages/RcsTest').then(m => ({ default: m.RcsTest })));
const CustomerQuestionnaire = lazyWithRetry(() => import('./pages/CustomerQuestionnaire').then(m => ({ default: m.CustomerQuestionnaire })));
const CustomerFeedback = lazyWithRetry(() => import('./pages/CustomerFeedback').then(m => ({ default: m.CustomerFeedback })));
const BillingDashboard = lazyWithRetry(() => import('./pages/billing/BillingDashboard').then(m => ({ default: m.BillingDashboard })));
const BillingAIUsage = lazyWithRetry(() => import('./pages/billing/BillingAIUsage').then(m => ({ default: m.BillingAIUsage })));
const BillingMessaging = lazyWithRetry(() => import('./pages/billing/BillingMessaging').then(m => ({ default: m.BillingMessaging })));
const BillingInvoices = lazyWithRetry(() => import('./pages/billing/BillingInvoices').then(m => ({ default: m.BillingInvoices })));
const EterminDebugPage = lazyWithRetry(() => import('./pages/debug/EterminDebugPage'));
const DataFixPage = lazyWithRetry(() => import('./pages/debug/DataFixPage'));
const DebugCalendar = lazyWithRetry(() => import('./pages/DebugCalendar'));
const DebugBooking = lazyWithRetry(() => import('./pages/DebugBooking'));
const LeadFollowUpDebug = lazyWithRetry(() => import('./pages/debug/LeadFollowUpDebug'));
const QuestionnaireDebug = lazyWithRetry(() => import('./pages/debug/QuestionnaireDebug'));
const RcsDeliveryTest = lazyWithRetry(() => import('./pages/debug/RcsDeliveryTest').then(m => ({ default: m.RcsDeliveryTest })));

/**
 * Handle expired sessions globally.
 * When any query/mutation returns UNAUTHORIZED mid-session,
 * redirect the user to login with a proper message.
 */
function handleGlobalAuthError(error: unknown) {
  const trpcError = error as { data?: { code?: string } };
  if (trpcError?.data?.code === 'UNAUTHORIZED') {
    // If the user had an active session, show "session expired" on login page
    if (localStorage.getItem('vitbikes_session_active')) {
      localStorage.removeItem('vitbikes_session_active');
      localStorage.setItem('vitbikes_session_expired', 'true');
      window.location.href = '/login';
    }
  }
}

const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: handleGlobalAuthError,
  }),
  mutationCache: new MutationCache({
    onError: handleGlobalAuthError,
  }),
  defaultOptions: {
    queries: {
      staleTime: 0, // Always refetch on mount — ensures tab switches load fresh data
      retry: (failureCount, error) => {
        // Don't retry UNAUTHORIZED errors
        const trpcError = error as { data?: { code?: string } };
        if (trpcError?.data?.code === 'UNAUTHORIZED') return false;
        return failureCount < 1;
      },
      refetchOnWindowFocus: false,
    },
  },
});

// Loading fallback for lazy-loaded pages
function PageLoader() {
  return (
    <div className="p-6 space-y-4" role="status" aria-label="Seite wird geladen">
      <Skeleton className="h-8 w-48" />
      <Skeleton className="h-64 w-full" />
      <Skeleton className="h-32 w-full" />
    </div>
  );
}

// Location-scoped routes (nested under /location/:locationSlug)
function LocationScopedRoutes() {
  return (
    <>
      <LocationSync />
      <Layout>
        <main id="main-content" tabIndex={-1} className="outline-none">
          <ErrorBoundary>
            <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route path="/" element={<Dashboard />} />
                <Route path="/contacts" element={<Contacts />} />
                <Route path="/contacts/:contactId" element={<Contacts />} />
                <Route path="/calendar" element={<Calendar />} />
                <Route path="/schulung" element={<Schulung />} />
                <Route path="/statistics" element={<Statistics />} />
                <Route path="/reports" element={<Reports />} />
                <Route path="/admin" element={<Admin />} />
                <Route path="/admin/:tab" element={<Admin />} />
                <Route path="/settings" element={<Settings />} />
                <Route path="/settings/:section" element={<Settings />} />
              </Routes>
            </Suspense>
          </ErrorBoundary>
        </main>
      </Layout>
    </>
  );
}

// Super Admin routes — outside location scope
function SuperAdminRoutes() {
  return (
    <Layout>
      <main id="main-content" tabIndex={-1} className="outline-none">
        <ErrorBoundary>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/" element={<Admin />} />
              <Route path="/:tab" element={<Admin />} />
            </Routes>
          </Suspense>
        </ErrorBoundary>
      </main>
    </Layout>
  );
}

// Protected app content with layout
function AppContent() {
  return (
    <ProtectedRoute>
      <Routes>
        {/* Super Admin route — outside location scope */}
        <Route path="/superadmin/*" element={<SuperAdminRoutes />} />
        {/* Location-scoped routes */}
        <Route path="/location/:locationSlug/*" element={<LocationScopedRoutes />} />
        {/* Root redirect → /location/:slug/ */}
        <Route path="/" element={<LocationRedirect />} />
        {/* Backwards compatibility: old URLs without /location/ prefix */}
        <Route path="/*" element={<LegacyRedirect />} />
      </Routes>
    </ProtectedRoute>
  );
}

export function App(): React.ReactElement {
  return (
    <ErrorBoundary>
      <trpc.Provider client={trpcClient} queryClient={queryClient}>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            {/* Skip to main content link for accessibility */}
            <a
              href="#main-content"
              className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:px-4 focus:py-2 focus:bg-orange-500 focus:text-white focus:rounded-lg"
            >
              Zum Hauptinhalt springen
            </a>
            <Routes>
              {/* Standalone billing dashboard — outside auth providers */}
              <Route path="/billing/*" element={
                <BillingLayout>
                  <Suspense fallback={<PageLoader />}>
                    <Routes>
                      <Route path="/" element={<BillingDashboard />} />
                      <Route path="/ai-usage" element={<BillingAIUsage />} />
                      <Route path="/messaging" element={<BillingMessaging />} />
                      <Route path="/invoices" element={<BillingInvoices />} />
                    </Routes>
                  </Suspense>
                </BillingLayout>
              } />

              <Route path="/*" element={
                <ThemeProvider>
                  <ToastProvider>
                    <AuthProvider>
                      <LocationProvider>
                        <Suspense fallback={<PageLoader />}>
                          <Routes>
                            {/* Public routes */}
                            <Route path="/login" element={<Login />} />
                            <Route path="/setup-2fa" element={<TwoFactorSetup />} />
                            <Route path="/support" element={<Support />} />
                            <Route path="/rcs-test" element={<RcsTest />} />
                            <Route path="/questionnaire/:locationId" element={<CustomerQuestionnaire />} />
                            <Route path="/feedback/:locationId" element={<CustomerFeedback />} />
                            <Route path="/debug/etermin" element={<EterminDebugPage />} />
                            <Route path="/debug/datafix" element={<DataFixPage />} />
                            <Route path="/debug/calendar" element={<DebugCalendar />} />
                            <Route path="/debug/booking" element={<DebugBooking />} />
                            <Route path="/debug/followups" element={<LeadFollowUpDebug />} />
                            <Route path="/debug/fragebogen" element={<QuestionnaireDebug />} />
                            <Route path="/debug/rcs-delivery" element={<RcsDeliveryTest />} />

                            {/* Protected routes */}
                            <Route path="/*" element={<AppContent />} />
                          </Routes>
                        </Suspense>
                      </LocationProvider>
                    </AuthProvider>
                  </ToastProvider>
                </ThemeProvider>
              } />
            </Routes>
          </BrowserRouter>
        </QueryClientProvider>
      </trpc.Provider>
    </ErrorBoundary>
  );
}
